import { useCallback, useEffect, useState } from 'react';
import {
  KeyRound, Loader2, AlertCircle, Search, UserPlus, X, Trash2, Pencil, Crown,
} from 'lucide-react';
import {
  listGmAccess, searchGmAccount, upsertGmAccess, revokeGmAccess,
  type GmAccessRow, type AccountSearchRow,
} from '@/shared/services/adminService';
import { useToast } from '@/shared/contexts/ToastContext';
import { DataTable } from '@/shared/components/ui/DataTable';
import { RowActions } from '@/shared/components/ui/RowActions';

const LEVEL_LABEL: Record<number, string> = {
  1: 'GM1',
  2: 'GM2',
  3: 'GM3',
  4: 'GM4',
  99: 'SUPER',
};

const LEVEL_DESC: Record<number, string> = {
  1: 'Dashboard, online, personagens, contas, ban, ver inventário',
  2: 'GM1 + enviar itens, monitor, editar character',
  3: 'GM2 + notícias, recargas (ver), premium, kits',
  4: 'GM3 + aprovar recargas, GM logs, editar cash',
};

export default function AccessPage() {
  const { notify } = useToast();
  const [items, setItems] = useState<GmAccessRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<AccountSearchRow[]>([]);
  const [editing, setEditing] = useState<AccountSearchRow | GmAccessRow | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try { setItems(await listGmAccess()); }
    catch (err: any) { setError(err?.detail || err?.message || 'Erro'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const doSearch = async () => {
    const q = searchTerm.trim();
    if (q.length < 2) { notify('error', 'Digite ao menos 2 caracteres'); return; }
    setSearching(true);
    try { setSearchResults(await searchGmAccount(q)); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
    finally { setSearching(false); }
  };

  const onRevoke = async (row: GmAccessRow) => {
    if (row.gmLevel === 99) {
      notify('error', 'Não é possível revogar o super-admin');
      return;
    }
    if (!confirm(`Revogar acesso GM de ${row.accountId || `UserNum ${row.userNum}`}?`)) return;
    try {
      await revokeGmAccess(row.userNum);
      notify('success', 'Acesso revogado');
      await fetchData();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <KeyRound className="text-amber-400" size={26} />
            Gestão de Acessos
          </h1>
          <p className="text-sm text-white/65 mt-1">
            {items.length} GM{items.length !== 1 && 's'} cadastrado{items.length !== 1 && 's'} · níveis 1 a 4
          </p>
        </div>
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Busca pra conceder */}
      <div className="surface-card rounded-xl p-5 mb-6">
        <p className="text-xs font-black uppercase tracking-[0.15em] text-white/55 mb-3">
          Conceder acesso a uma conta
        </p>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && doSearch()}
              placeholder="Buscar por ID, email ou UserNum..."
              className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg
                         pl-9 pr-3 py-2.5 text-sm text-white"
            />
          </div>
          <button
            onClick={doSearch}
            disabled={searching}
            className="px-5 rounded-lg bg-amber-500 hover:bg-amber-400
                       text-[#0d141b] font-bold text-sm transition cursor-pointer
                       disabled:opacity-50 flex items-center gap-2"
          >
            {searching && <Loader2 size={14} className="animate-spin" />}
            Buscar
          </button>
        </div>

        {searchResults.length > 0 && (
          <div className="mt-4 border border-white/[0.06] rounded-lg overflow-hidden">
            {searchResults.map(r => (
              <div key={r.userNum}
                   className="flex items-center gap-3 px-4 py-2.5 border-b border-white/[0.04]
                              last:border-0 hover:bg-white/[0.02]">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white">{r.accountId}</p>
                  <p className="text-[11px] text-white/45 font-mono">
                    UserNum {r.userNum}{r.email ? ` · ${r.email}` : ''}
                  </p>
                </div>
                {r.hasGm && r.gmLevel ? (
                  <span className="text-[10px] px-2 py-0.5 rounded
                                   bg-amber-500/15 text-amber-400 font-bold">
                    {LEVEL_LABEL[r.gmLevel] || `LV ${r.gmLevel}`}
                  </span>
                ) : null}
                <button
                  onClick={() => setEditing(r)}
                  disabled={r.gmLevel === 99}
                  className="px-3 py-1.5 rounded-lg bg-[#00e1d7]/10 hover:bg-[#00e1d7]/20
                             text-[#00e1d7] text-xs font-bold transition cursor-pointer
                             disabled:opacity-30 disabled:cursor-not-allowed
                             flex items-center gap-1"
                >
                  <UserPlus size={12} />
                  {r.hasGm ? 'Alterar' : 'Conceder'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lista GMs */}
      <p className="text-xs font-black uppercase tracking-[0.15em] text-white/55 mb-2">
        GMs ativos
      </p>
      <DataTable<GmAccessRow>
        columns={[
          { key: 'accountId', label: 'Conta', width: '1.5fr',
            cell: (it) => (
              <div className="min-w-0">
                <p className="text-sm font-bold text-white truncate">{it.accountId || `UserNum ${it.userNum}`}</p>
                <p className="text-[10px] text-white/45 mt-0.5 font-mono">
                  UserNum {it.userNum}
                  {it.grantedById ? ` · por ${it.grantedById}` : ''}
                </p>
              </div>
            ) },
          { key: 'level', label: 'Nível', width: '130px',
            cell: (it) => (
              <span className={`text-[10px] px-2 py-0.5 rounded font-black inline-flex items-center gap-1
                              ${it.gmLevel === 99
                                ? 'bg-amber-500/20 text-amber-300 border border-amber-400/40'
                                : 'bg-amber-500/10 text-amber-400'}`}>
                {it.gmLevel === 99 ? <><Crown size={10} /> SUPER</> : (LEVEL_LABEL[it.gmLevel] || `LV ${it.gmLevel}`)}
              </span>
            ) },
          { key: 'grantedAt', label: 'Concedido', width: '180px', mobileHidden: true,
            cell: (it) => (
              <span className="text-[11px] text-white/55 font-mono">
                {it.grantedAt ? new Date(it.grantedAt).toLocaleString('pt-BR') : '—'}
              </span>
            ) },
          { key: 'notes', label: 'Notas', width: '1fr', mobileHidden: true,
            cell: (it) => (
              <span className="text-[11px] text-white/65 italic truncate" title={it.notes ?? ''}>
                {it.notes ?? '—'}
              </span>
            ) },
          { key: 'actions', label: 'Ações', width: '60px', align: 'right', mobileHidden: true,
            cell: (it) => it.gmLevel !== 99 ? (
              <RowActions
                actions={[
                  { label: 'Alterar nível', icon: <Pencil size={13} />, onClick: () => setEditing(it), variant: 'warning' },
                  { label: 'Revogar', icon: <Trash2 size={13} />, onClick: () => onRevoke(it), variant: 'danger' },
                ]}
              />
            ) : null },
        ]}
        rows={items}
        rowKey={(it) => it.userNum}
        loading={loading}
        empty="Nenhum GM cadastrado"
        mobileTitle={(it) => it.accountId || `UserNum ${it.userNum}`}
        mobileSubtitle={(it) => it.gmLevel === 99 ? 'SUPER ADMIN' : (LEVEL_LABEL[it.gmLevel] || `LV ${it.gmLevel}`)}
        mobileActions={(it) => it.gmLevel !== 99 ? (
          <RowActions
            actions={[
              { label: 'Alterar nível', icon: <Pencil size={13} />, onClick: () => setEditing(it), variant: 'warning' },
              { label: 'Revogar', icon: <Trash2 size={13} />, onClick: () => onRevoke(it), variant: 'danger' },
            ]}
            align="left"
            
          />
        ) : null}
      />

      {/* Editor modal */}
      {editing && (
        <AccessEditor
          target={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            await fetchData();
            setEditing(null);
            // re-busca a lista pra atualizar status do search
            if (searchResults.length > 0) doSearch();
          }}
        />
      )}
    </div>
  );
}


// ----------------------------------------------------------------- Editor

function isAccessRow(x: any): x is GmAccessRow {
  return typeof x?.gmLevel === 'number' && x?.grantedAt !== undefined;
}

function AccessEditor({
  target, onClose, onSaved,
}: {
  target: AccountSearchRow | GmAccessRow;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { notify } = useToast();
  const userNum = target.userNum;
  const accountId = isAccessRow(target) ? target.accountId : target.accountId;
  const currentLevel = isAccessRow(target) ? target.gmLevel : (target.gmLevel ?? 0);
  const currentNotes = isAccessRow(target) ? target.notes : null;

  const [level, setLevel] = useState<number>(currentLevel && currentLevel <= 4 ? currentLevel : 1);
  const [notes, setNotes] = useState<string>(currentNotes || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await upsertGmAccess(userNum, { gmLevel: level, notes: notes || undefined });
      notify('success', currentLevel ? 'Nível alterado' : 'Acesso concedido');
      await onSaved();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro salvando');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl p-6 max-w-md w-full"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-lg font-bold text-white">
              {currentLevel ? 'Alterar nível' : 'Conceder acesso'}
            </h3>
            <p className="text-[11px] text-white/55 font-mono mt-1">
              {accountId} · UserNum {userNum}
            </p>
          </div>
          <button onClick={onClose} className="text-white/55 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-2 mb-5">
          {[1, 2, 3, 4].map(lv => (
            <button
              key={lv}
              type="button"
              onClick={() => setLevel(lv)}
              className={`w-full text-left px-4 py-3 rounded-lg transition cursor-pointer
                          ${level === lv
                            ? 'bg-amber-500/15 border border-amber-400/40'
                            : 'bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06]'}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-sm font-black ${level === lv ? 'text-amber-400' : 'text-white'}`}>
                  GM{lv}
                </span>
                {level === lv && (
                  <span className="text-[10px] font-bold text-amber-400">ESCOLHIDO</span>
                )}
              </div>
              <p className="text-[11px] text-white/65">{LEVEL_DESC[lv]}</p>
            </button>
          ))}
        </div>

        <div className="mb-5">
          <label className="text-[11px] text-white/55 mb-1 block">Notas (opcional)</label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="ex: GM equipe de eventos"
            maxLength={255}
            className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg
                       px-3 py-2 text-sm text-white"
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-lg bg-white/[0.04] text-white/75
                       hover:bg-white/[0.08] transition cursor-pointer text-sm"
          >
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={busy}
            className="flex-1 px-4 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400
                       text-[#0d141b] font-bold text-sm transition cursor-pointer
                       disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}
